import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  allPromptOutputsSelector,
  useDesignerDataContext,
} from '../../../../hooks/useDesignerLogic';
import { useDesignerUIContext } from '../../../../hooks/useDesignerUIState';
import { useGenerateExampleAnswers } from '../../../../hooks/useGenerateExampleAnswers';
import {
  getAvailablePromptDependencies,
  useVisiblePromptDependentInputValues,
  useVisiblePromptTestCaseInputs,
} from '../../../../hooks/useVisiblePromptTestCaseInputs';
import {
  ById,
  GeneratedStudentResponse,
  LocalPromptDependency,
  Prompt,
  PromptInput,
} from '../../../../services/backendService/types';
import { Button } from '../../../_atoms/Button';
import { parseSlateStringToText } from '../../../SlideComponents/slateUtil';
import { Slide } from '../../../SlideComponents/slide.types';
import { findDependencies } from '../PromptsTab/PromptBuilder';
import { PromptTextEditor } from '../PromptsTab/PromptTextEditor';
import {
  createStudentAnswerPrompt,
  getStudentAnswerTypes,
  SLATE_STUDENT_CATEGORY_INPUT_ID,
} from './studentAnswerUtils';

export const GenerateExamples = ({
  prompt,
  promptInputs,
  slide,
  onStudentAnswersGenerated,
  studentAnswerExamplePrompt,
  presentationId,
}: {
  prompt: Prompt;
  promptInputs?: PromptInput[];
  slide: Slide;
  presentationId: string;
  onStudentAnswersGenerated: (answers: GeneratedStudentResponse[]) => void;
  studentAnswerExamplePrompt: string;
}) => {
  const [promptContent, setPromptContent] = useState<string>(studentAnswerExamplePrompt);

  const promptOutputs = useDesignerDataContext(allPromptOutputsSelector);
  const promptDependentInputs = useDesignerUIContext(
    context => context.state.promptDependentInputs,
  );

  const { generate, data, isLoading } = useGenerateExampleAnswers(
    presentationId,
    slide.id,
  );

  useEffect(() => {
    if (data) {
      onStudentAnswersGenerated(
        getStudentAnswerTypes(slide.id).map(t => ({
          ...t,
          response: data[`slide-question-answer.${t.responseId}`]?.value || '',
        })),
      );
    }
  }, [data, onStudentAnswersGenerated, slide.id]);

  const dependencies = useMemo(
    () => findDependencies(createStudentAnswerPrompt(promptContent)),
    [promptContent],
  );

  const availablePromptDependencies = useMemo(() => {
    const studentCategoryField: LocalPromptDependency = {
      id: SLATE_STUDENT_CATEGORY_INPUT_ID,
      type: 'live',
      label: `Student response category`,
      formType: 'shortText',
    };

    const questionField: LocalPromptDependency = {
      id: `slide-question`,
      type: 'live',
      label: `Slide question`,
      formType: 'shortText',
    };

    const generalAvailablePromptDependencies = getAvailablePromptDependencies({
      promptId: prompt.id,
      promptOutputs,
      promptInputs,
    });

    return [...generalAvailablePromptDependencies, studentCategoryField, questionField];
  }, [prompt.id, promptInputs, promptOutputs]);

  const { visibleInputs } = useVisiblePromptTestCaseInputs({
    availablePromptDependencies,
    promptDependencies: dependencies,
    promptInputs,
  });

  const { visiblePromptDependentInputValues } = useVisiblePromptDependentInputValues({
    visibleInputs,
    promptDependentInputs,
  });

  const question = useMemo(() => {
    const aiQuestionElement = slide.elements.find(
      element =>
        element.lockedFromDeletion &&
        (element.type === 'text' || element.type === 'generatorText'),
    );
    if (aiQuestionElement) {
      return parseSlateStringToText(aiQuestionElement.textValue);
    }
  }, [slide]);

  const handleGenerate = () => {
    let testCaseInputs: ById<string> = {};

    // add default values form the dependencies themselves
    dependencies.forEach(dependency => {
      const foundInput = promptInputs?.find(
        input => input.id === dependency.dependencyId,
      );

      if (foundInput && foundInput.defaultValue) {
        testCaseInputs = {
          ...testCaseInputs,
          [dependency.dependencyId]: foundInput.defaultValue as string,
        };
      }
    });

    // Overwrite the default values with the prompt dependent inputs
    Object.values(visiblePromptDependentInputValues).forEach(dependency => {
      testCaseInputs = {
        ...testCaseInputs,
        [dependency.id]: dependency.value,
      };
    });
    const inputData = Object.keys(testCaseInputs).filter(key => key.startsWith('input'));
    const outputData = Object.keys(testCaseInputs).filter(key =>
      key.startsWith('prompt'),
    );
    // Make sure to add our all inputs
    const userInputFormData = inputData.map(key => ({
      id: key,
      type: 'string',
      value: testCaseInputs[key],
    }));

    // Make sure to add our custom slide-question slate element
    userInputFormData.push({
      id: 'slide-question',
      type: 'string',
      value: parseSlateStringToText(question),
    });

    generate({
      prompt: promptContent,
      userInputFormData,
      categories: getStudentAnswerTypes(slide.id).map(type => ({
        id: type.responseId,
        prompt: type.responsePrompt,
      })),
      resourceMap: outputData.reduce(
        (acc, key) => ({
          ...acc,
          [key]: {
            id: key,
            type: 'string',
            value: testCaseInputs[key],
          },
        }),
        {},
      ),
    });
  };

  return (
    <PromptContainer style={{ paddingTop: 40 }}>
      <PromptTextEditor
        value={promptContent}
        slideId={'my-mega-prompt'}
        placeholder={'Generate an example...'}
        availablePromptDependencies={availablePromptDependencies}
        onChange={val => setPromptContent(val)}
        maxLength={6000}
      />
      <Button
        loading={isLoading}
        styles={{ marginTop: 20 }}
        type="primary"
        onClick={handleGenerate}
      >
        Generate
      </Button>
    </PromptContainer>
  );
};

const PromptContainer = styled.div`
  p {
    font-size: 14px;
    font-weight: bold;
    color: #282f33;
  }
`;
